import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { getError } from '../utils';
import Badge from 'react-bootstrap/Badge';
import { useTranslation } from 'react-i18next';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        usersInformation: action.payload.usersInformation,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };
    default:
      return state;
  }
};

export default function UserEditScreen() {
  const [{ loading, error, loadingUpdate, usersInformation }, dispatch] =
    useReducer(reducer, {
      loading: true,
      error: '',
      usersInformation: [],
    });

  const { t } = useTranslation();

  const params = useParams();
  const { id: userId } = params;

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [tempEmail, setTempEmail] = useState('');
  // const [isCustomerSupportAgent, setIsCustomerSupportAgent] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [deletedUserAccount, setDeletedUserAccount] = useState(false);
  const [isResending, setIsResending] = useState(false); // State variable to manage resend button disabled state
  const [triggerFetchRequest, setTriggerFetchRequest] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await api.get(`/api/users/${userId}`);
        setUsername(data.username);
        setEmail(data.email);
        setTempEmail(data.tempEmail || '');
        setIsAdmin(data.isAdmin);
        setIsVerified(data.isVerified);
        setDeletedUserAccount(data.deletedUserAccount);

        // setIsCustomerSupportAgent(data.isCustomerSupportAgent);
        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            usersInformation: data,
          },
        });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [userId, triggerFetchRequest]);

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      const baseUrl = window.location.origin;

      await api.put(`/api/users/${userId}`, {
        username,
        email,
        isVerified,
        /* isCustomerSupportAgent, */ isAdmin,
        baseUrl,
      });
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      setTriggerFetchRequest(!triggerFetchRequest);
      toast.success('Benutzer aktualisiert');
    } catch (error) {
      const errorMessage = getError(error);
      if (errorMessage.includes('nice try \u{1F602}')) {
        toast.error(errorMessage, {
          className: 'nice-try-toast',
        });
      } else {
        toast.error(t(getError(error)));
      }

      setUsername(usersInformation.username);
      setEmail(usersInformation.email);
      setTempEmail(usersInformation.tempEmail || '');
      setIsAdmin(usersInformation.isAdmin);
      setIsVerified(usersInformation.isVerified);

      dispatch({ type: 'UPDATE_FAIL' });
    }
  };

  const resendVerificationEmail = async (userId) => {
    setIsResending(true); // Disable the button when the function starts
    try {
      const baseUrl = window.location.origin; // Get the base URL
      await api.post(`/api/users/admin/resend-verification-email/${userId}`, {
        baseUrl,
      });
      toast.success('Verification email resent');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setIsResending(false); // Re-enable the button when the function completes
    }
  };

  const withdrawEmailRequest = async (userId) => {
    try {
      await api.post(`/api/users/admin/withdraw-email-request/${userId}`, {});
      setTriggerFetchRequest(!triggerFetchRequest);
      toast.success('Email update request withdrawn');
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <Container>
      <Container className="small-container">
        <title>Benutzer bearbeiten</title>

        <h1 className="screen-heading">Benutzer bearbeiten</h1>

        <p className="product-id-heading">(ID: {userId})</p>

        {loading || loadingUpdate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Form onSubmit={submitHandler}>
              <Form.Group className="mb-3" controlId="username">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  className="mobile-device-compatible"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={deletedUserAccount}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  className="mobile-device-compatible"
                  value={email}
                  type="email"
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={deletedUserAccount}
                />
                {tempEmail && (
                  <Form.Text
                    className="text-muted"
                    style={{
                      fontSize: '14px',
                      lineHeight: '1.2',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      variant="none"
                      onClick={() =>
                        withdrawEmailRequest(usersInformation.userId)
                      }
                    >
                      <svg
                        fill="currentColor"
                        width="14"
                        height="14"
                        viewBox="0 0 56 56"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginBottom: '1px' }}
                      >
                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                        <g
                          id="SVGRepo_tracerCarrier"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></g>
                        <g id="SVGRepo_iconCarrier">
                          <path d="M 44.5235 48.6602 L 46.1407 14.3945 L 48.4844 14.3945 C 49.4454 14.3945 50.2187 13.5976 50.2187 12.6367 C 50.2187 11.6758 49.4454 10.8555 48.4844 10.8555 L 38.2422 10.8555 L 38.2422 7.3398 C 38.2422 3.9883 35.9688 1.8086 32.3595 1.8086 L 23.5938 1.8086 C 19.9844 1.8086 17.7344 3.9883 17.7344 7.3398 L 17.7344 10.8555 L 7.5391 10.8555 C 6.6016 10.8555 5.7813 11.6758 5.7813 12.6367 C 5.7813 13.5976 6.6016 14.3945 7.5391 14.3945 L 9.8829 14.3945 L 11.5000 48.6836 C 11.6641 52.0586 13.8907 54.1914 17.2657 54.1914 L 38.7579 54.1914 C 42.1095 54.1914 44.3595 52.0351 44.5235 48.6602 Z M 21.4844 7.5742 C 21.4844 6.2383 22.4688 5.3008 23.8751 5.3008 L 32.1016 5.3008 C 33.5313 5.3008 34.5157 6.2383 34.5157 7.5742 L 34.5157 10.8555 L 21.4844 10.8555 Z M 17.6173 50.6758 C 16.2579 50.6758 15.2500 49.6445 15.1797 48.2852 L 13.5391 14.3945 L 42.3907 14.3945 L 40.8438 48.2852 C 40.7735 49.6680 39.7891 50.6758 38.4063 50.6758 Z M 34.9610 46.5508 C 35.7344 46.5508 36.3204 45.9180 36.3438 45.0273 L 37.0469 20.2773 C 37.0704 19.3867 36.4610 18.7305 35.6641 18.7305 C 34.9376 18.7305 34.3282 19.4102 34.3048 20.2539 L 33.6016 45.0273 C 33.5782 45.8711 34.1641 46.5508 34.9610 46.5508 Z M 21.0626 46.5508 C 21.8595 46.5508 22.4454 45.8711 22.4219 45.0273 L 21.7188 20.2539 C 21.6954 19.4102 21.0626 18.7305 20.3360 18.7305 C 19.5391 18.7305 18.9532 19.3867 18.9766 20.2773 L 19.7032 45.0273 C 19.7266 45.9180 20.2891 46.5508 21.0626 46.5508 Z M 29.4298 45.0273 L 29.4298 20.2539 C 29.4298 19.4102 28.7969 18.7305 28.0235 18.7305 C 27.2500 18.7305 26.5938 19.4102 26.5938 20.2539 L 26.5938 45.0273 C 26.5938 45.8711 27.2500 46.5508 28.0235 46.5508 C 28.7735 46.5508 29.4298 45.8711 29.4298 45.0273 Z"></path>
                        </g>
                      </svg>
                    </Button>
                    <Button
                      variant="light"
                      onClick={() =>
                        resendVerificationEmail(usersInformation.userId)
                      }
                      disabled={isResending} // Disable the button when resending
                      style={{
                        marginRight: '5px',
                      }}
                    >
                      Resend
                    </Button>
                    {tempEmail}{' '}
                    <Badge
                      style={{
                        fontSize: '12px',
                        marginLeft: '5px',
                        display: 'flex',
                      }}
                      bg="info"
                    >
                      verification pending
                    </Badge>
                  </Form.Text>
                )}
              </Form.Group>
              <Form.Group controlId="Verified">
                <Form.Label>Verified</Form.Label>
                <Form.Select
                  className="mobile-device-compatible"
                  value={isVerified ? 'yes' : 'no'}
                  onChange={(e) => setIsVerified(e.target.value === 'yes')}
                  disabled={deletedUserAccount}
                >
                  <option value="yes">Ja</option>
                  <option value="no">Nein</option>
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="admin">
                <Form.Label>Admin</Form.Label>
                <Form.Select
                  className="mobile-device-compatible"
                  value={isAdmin ? 'yes' : 'no'}
                  onChange={(e) => setIsAdmin(e.target.value === 'yes')}
                  disabled={deletedUserAccount}
                >
                  <option value="yes">Ja</option>
                  <option value="no">Nein</option>
                </Form.Select>
              </Form.Group>

              <div className="mb-3 text-center mt-5">
                <Button
                  variant="secondary"
                  disabled={loadingUpdate}
                  type="submit"
                >
                  Speichern
                </Button>
              </div>
            </Form>
          </>
        )}
      </Container>
    </Container>
  );
}
